import { createContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [token, setTokenState] = useState(
    () => localStorage.getItem('access_token') || '',
  );
  const [currentUser, setCurrentUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [accessibleGroups, setAccessibleGroups] = useState([]);
  const [accessibleHotels, setAccessibleHotels] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);

  // instância dedicada às chamadas autenticadas
  const axiosAuth = useMemo(() => {
    return axios.create({
      // Em dev, REACT_APP_API_BASE aponta para '/api/v1' (relativo) e o
      // setupProxy.js reencaminha para o Render, evitando CORS no browser.
      // Em produção a variável não existe e usa-se o URL absoluto.
      baseURL:
        process.env.REACT_APP_API_BASE ||
        'https://hoteisdigitais-backend.onrender.com/api/v1',
      timeout: 15000,
    });
  }, []);

  // aplica / retira o Authorization sempre que o token muda
  useEffect(() => {
    if (token) {
      localStorage.setItem('access_token', token);
      axiosAuth.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      localStorage.removeItem('access_token');
      delete axiosAuth.defaults.headers.common.Authorization;
    }
  }, [token, axiosAuth]);

  // função pública para atualizar o utilizador atual
  const refreshUser = async () => {
    if (!token) {
      setCurrentUser(null);
      return;
    }
    try {
      const { data } = await axiosAuth.get('/users/me');
      setCurrentUser(data);
    } catch (err) {
      console.error(
        'Erro ao obter current user:',
        err.response?.data || err.message,
      );
      setCurrentUser(null);
      setTokenState(''); // limpa token inválido
    }
  };

  // obtém grupos / hotéis acessíveis ao utilizador logado
  const refreshAccess = async () => {
    if (!token || !currentUser) {
      setAccessibleGroups([]);
      setAccessibleHotels([]);
      return;
    }
    setAccessLoading(true);
    try {
      // não é preciso repetir o header Authorization aqui; o axiosAuth já tem
      const [groupsRes, hotelsRes] = await Promise.all([
        axiosAuth.get('/groups/accessible'),
        axiosAuth.get('/hotels/accessible'),
      ]);
      setAccessibleGroups(groupsRes.data || []);
      setAccessibleHotels(hotelsRes.data || []);
    } catch (err) {
      console.error('Erro ao obter acesso:', err.response?.data || err.message);
      setAccessibleGroups([]);
      setAccessibleHotels([]);
    } finally {
      setAccessLoading(false);
    }
  };

  // tenta restaurar sessão ao montar / quando o token muda
  useEffect(() => {
    (async () => {
      await refreshUser();
      setReady(true);
    })();
  }, [token]);

  // quando currentUser muda (ou token), atualiza acessos
  useEffect(() => {
    if (!token || !currentUser) {
      setAccessibleGroups([]);
      setAccessibleHotels([]);
      return;
    }
    refreshAccess();
  }, [token, currentUser]);

  // logout global ao receber 401 em qualquer request
  useEffect(() => {
    const respInterceptor = axiosAuth.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error?.response?.status === 401) {
          setTokenState('');
          setCurrentUser(null);
          setAccessibleGroups([]);
          setAccessibleHotels([]);
          navigate('/login', {
            replace: true,
          });
        }
        return Promise.reject(error);
      },
    );
    return () => axiosAuth.interceptors.response.eject(respInterceptor);
  }, [axiosAuth, navigate]);

  // sincroniza token entre separadores
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'access_token') {
        setTokenState(e.newValue || '');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setToken = (t) => setTokenState(t);

  const logout = () => {
    setTokenState('');
    setAccessibleGroups([]);
    setAccessibleHotels([]);
    setCurrentUser(null);
    navigate('/login', {
      replace: true,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        currentUser,
        refreshUser,
        logout,
        axiosAuth,
        ready,
        accessibleGroups,
        accessibleHotels,
        accessLoading,
        refreshAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
